import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import * as Sentry from '@sentry/browser'
import { COPY_STRINGS } from '../../../../constants/translatedCopy'
import { PRE_CHECK_MACHINE_STATES } from '../../constants'
import useStudentPreChecks from '../../useStudentPreChecks'
import { ASR_EVENTS } from '../StudentASRConstants'
import { selectReaderManager } from '../../../Reader/ReaderManagerEvents/ReaderManagerSlice'
import { getCopyByGradeband } from '../../../../services/ThemingService'

const MS_TO_REMIND_TO_SPEAK = 6000 // 6 seconds
const MAX_TEACHER_MODE_TRY_COUNT = 1 // how many times to remind the teacher to test before ending it
const MAX_REMIND_TO_SPEAK_COUNT = 3 // how many times to remind the student before calling teacher

export default function ASRInitialSpeechReminders(props) {

    const {speechProps, teacherMode, openRetryModal, history, setShowTeacherModal, readerManagerRef} = props
    const readerManagerStates = useSelector(selectReaderManager)

    const studentPreCheck = useStudentPreChecks({ itemToCheck: PRE_CHECK_MACHINE_STATES.studentASRChecks })

    const remindToSpeakTimer = useRef()
    const remindToSpeakCount = useRef(0)
    // Use ref to track latest receivedValidTranscript value to avoid stale closure
    const receivedTranscriptRef = useRef(readerManagerStates.contextStates.receivedValidTranscript)

    // Update ref whenever receivedValidTranscript changes
    useEffect(() => {
        receivedTranscriptRef.current = readerManagerStates.contextStates.receivedValidTranscript
    }, [readerManagerStates.contextStates.receivedValidTranscript])

    useEffect(()=>{

        if (readerManagerStates.isCurrentStoryInitialPhraseStarted) {
            if(teacherMode && remindToSpeakCount.current <= MAX_TEACHER_MODE_TRY_COUNT){
                startReminderToSpeakTimer()
            }else if(!teacherMode && remindToSpeakCount.current <= MAX_REMIND_TO_SPEAK_COUNT){
                startReminderToSpeakTimer()
            }
        }

        return () => {
            cancelReminderToSpeak()
        }
    }, [remindToSpeakCount.current, readerManagerStates.isCurrentStoryInitialPhraseStarted])

    const startReminderToSpeakTimer = () => {
        cancelReminderToSpeak() // cancel existing one first
        remindToSpeakTimer.current = setTimeout(()=>{
            remindToSpeak()
        }, MS_TO_REMIND_TO_SPEAK)
    }
    
    const cancelReminderToSpeak = () => {
        clearTimeout(remindToSpeakTimer.current)
    }

    const remindToSpeak = async () => {
        const remindCount = remindToSpeakCount.current
        const studentChecks = getCopyByGradeband(COPY_STRINGS, 'student_checks');
        
        if(teacherMode){
            studentPreCheck.logEvent(ASR_EVENTS.TEACHER_TEST_PROMPT_SPEECH)
            if(remindCount == 0){
                await speechProps.speakWithPromise({textToSpeak: studentChecks.teacher_mode_asr_reminder_one})
            }else{
                // Teacher test failed - redirect back to teacher modal with timeout result
                // Use the same pattern as openTeacherModal to prevent redirect loops
                speechProps.stopListening()
                setShowTeacherModal(true)
                history.push('/student/pre-checks?teacherTestResult=timeout')
                return; // Terminal state - don't increment counter or trigger another timeout
            }
        }else{
            studentPreCheck.logEvent(ASR_EVENTS.STUDENT_PROMPT_SPEECH)
            
            if(remindCount == 0){
                await speechProps.speakWithPromise({textToSpeak: studentChecks.student_asr_reminder_one})
            }else if(remindCount == 1){
                // Only call onPhraseTimeout if we've received a transcript
                // Use ref to get latest value to avoid stale closure
                if (receivedTranscriptRef.current && readerManagerRef && readerManagerRef.current && readerManagerRef.current.onPhraseTimeout) {
                    readerManagerRef.current.onPhraseTimeout();
                    return;
                } else {
                    // No transcript received, open retry modal
                    openRetryModal()
                    return;
                }
            } else {
                // Fallback to original behavior if ref is not available
                openRetryModal()
            }
        }
        remindToSpeakCount.current = remindCount + 1
    }

    return null
}
